import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Oval } from "react-loader-spinner";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { FaTint, FaSearch, FaEdit, FaTrashAlt, FaCheck, FaTimes, FaSpinner, FaBuilding } from 'react-icons/fa';
import { StandardURL } from "../App";


const PropertyWaterBillPage = () => {
    const [propertyId, setPropertyId] = useState("");
    const [waterBills, setWaterBills] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false); // New state for update action loading
    const [currentUnits, setCurrentUnits] = useState("");
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [billToUpdate, setBillToUpdate] = useState(null);

    const token = localStorage.getItem("token");

    const fetchWaterBills = () => {
        if (!propertyId) {
            toast.error("Please enter a Property ID to search.");
            return;
        }

        setIsLoading(true);
        setWaterBills([]);

        axios
            .get(
                `${StandardURL}/iNyumba/admin/waterbill/getall/${propertyId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then((response) => {
                setWaterBills(response.data.response);
                toast.success(`Found ${response.data.response.length} water bills for Property ID: ${propertyId}`);
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.response || "Error fetching water bills. Check the Property ID.";
                console.error("Error fetching water bills:", error);
                toast.error(errorMessage);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleDeleteWaterBill = (waterBillId) => {
        if (!window.confirm(`Are you sure you want to delete Water Bill ID: ${waterBillId}?`)) {
            return;
        }

        axios
            .delete(
                `${StandardURL}/iNyumba/admin/waterbill/deletewaterbill/${waterBillId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then(() => {
                toast.success("Water bill deleted successfully.");
                fetchWaterBills();
            })
            .catch((error) => {
                console.error("Error deleting water bill:", error);
                toast.error("Failed to delete water bill.");
            });
    };

    const handleUpdateWaterBillSubmit = () => {
        if (!billToUpdate || !currentUnits) {
            toast.error("Missing bill data or current units.");
            return;
        }

        setIsUpdating(true);

        const data = {
            currentunits: currentUnits,
        };

        axios
            .patch(
                `${StandardURL}/iNyumba/admin/waterbill/updatewaterbillid/${billToUpdate.waterbill_id}`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then(() => {
                toast.success("Water bill updated successfully.");
                // Reset states
                setUpdateModalOpen(false);
                setCurrentUnits("");
                setBillToUpdate(null);
                fetchWaterBills();
            })
            .catch((error) => {
                console.error("Error updating water bill:", error);
                toast.error("Failed to update water bill.");
            })
            .finally(() => {
                setIsUpdating(false);
            });
    };
    
    // Function to open the modal and set initial data
    const openUpdateModal = (bill) => {
        setBillToUpdate(bill);
        // Set the input field to the existing value for editing
        setCurrentUnits(bill.currentunits || ''); 
        setUpdateModalOpen(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch {
            return dateString;
        }
    }

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    <div className="max-w-6xl mx-auto bg-white shadow-2xl rounded-xl p-6 lg:p-8">

                        {/* Header */}
                        <h1 className="text-3xl font-extrabold text-gray-900 mb-6 flex items-center border-b pb-4">
                            <FaTint className="text-blue-600 mr-3" /> Property Water Bills Management
                        </h1>

                        {/* Search Bar */}
                        <div className="mb-8 p-4 bg-blue-50 rounded-lg shadow-inner">
                            <label htmlFor="property-id-input" className="block text-sm font-medium text-blue-800 mb-2 flex items-center">
                                <FaBuilding className="mr-2" /> Enter Property ID to view bills
                            </label>
                            <div className="flex space-x-3">
                                <input
                                    type="text"
                                    id="property-id-input"
                                    value={propertyId}
                                    onChange={(e) => setPropertyId(e.target.value)}
                                    placeholder="Property ID (e.g., PROP-001)"
                                    className="flex-grow border border-gray-300 rounded-lg py-2 px-4 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                                />
                                <button
                                    onClick={fetchWaterBills}
                                    disabled={isLoading}
                                    className={`inline-flex items-center justify-center font-bold py-2 px-4 rounded-lg shadow-md transition duration-200 text-white 
                                        ${isLoading
                                            ? "bg-blue-400 cursor-not-allowed"
                                            : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 focus:ring-2 focus:ring-offset-2"
                                        } disabled:opacity-80`}
                                >
                                    {isLoading ? (
                                        <FaSpinner className="animate-spin w-5 h-5" />
                                    ) : (
                                        <FaSearch className="w-5 h-5 mr-2" />
                                    )}
                                    Search
                                </button>
                            </div>
                        </div>

                        {/* Loading State */}
                        {isLoading && (
                            <div className="flex justify-center py-12">
                                <Oval height={40} width={40} color="#2563eb" ariaLabel="loading-indicator" />
                                <p className="ml-3 text-lg text-blue-600">Fetching water bills...</p>
                            </div>
                        )}

                        {/* Results Table */}
                        {!isLoading && waterBills.length > 0 && (
                            <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200 mt-6">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill ID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit #</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rental ID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Prev. Units</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Curr. Units</th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {waterBills.map((bill, index) => (
                                            <tr key={bill.waterbill_id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(bill.created_at)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">{bill.waterbill_id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{bill.house_number}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{bill.rental_id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{bill.user_id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-right">{bill.previousunits}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-right text-red-600">{bill.currentunits}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium space-x-2">
                                                    <button
                                                        onClick={() => openUpdateModal(bill)}
                                                        className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-50 transition"
                                                        title="Update Current Units"
                                                    >
                                                        <FaEdit className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteWaterBill(bill.waterbill_id)}
                                                        className="text-red-600 hover:text-red-800 p-2 rounded-full hover:bg-red-50 transition"
                                                        title="Delete Bill"
                                                    >
                                                        <FaTrashAlt className="w-5 h-5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        
                        {!isLoading && waterBills.length === 0 && propertyId && (
                            <div className="text-center p-10 text-gray-500 border-2 border-dashed border-gray-300 rounded-xl mt-6">
                                <FaTint className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                <p className="text-lg">No water bills found for Property ID: **{propertyId}**.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* --- Update Modal (Full-Screen Overlay) --- */}
                {updateModalOpen && billToUpdate && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            
                            {/* Background Overlay */}
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

                            {/* Modal Content Container */}
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            
                            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <FaEdit className="h-6 w-6 text-blue-600" />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                Update Water Bill for Unit {billToUpdate.house_number}
                                            </h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Bill ID: **{billToUpdate.waterbill_id}** | Prev. Units: **{billToUpdate.previousunits}**
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="mt-5">
                                        <label htmlFor="currentUnits" className="block text-sm font-medium text-gray-700 mb-2">
                                            Enter New Current Units
                                        </label>
                                        <input
                                            type="number"
                                            id="currentUnits"
                                            value={currentUnits}
                                            onChange={(e) => setCurrentUnits(e.target.value)}
                                            placeholder="e.g., 1200"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition"
                                            min={billToUpdate.previousunits || 0} // Basic validation
                                        />
                                        {parseFloat(currentUnits) < parseFloat(billToUpdate.previousunits) && (
                                            <p className="mt-2 text-sm text-red-600">Current units should be greater than previous units.</p>
                                        )}
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                                    <button
                                        onClick={handleUpdateWaterBillSubmit}
                                        disabled={isUpdating || !currentUnits || parseFloat(currentUnits) < parseFloat(billToUpdate.previousunits)}
                                        className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-6 py-3 text-base font-medium text-white transition duration-300 ease-in-out sm:w-auto sm:text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 
                                            ${isUpdating
                                                ? "bg-blue-400 cursor-not-allowed"
                                                : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
                                            } disabled:opacity-70`}
                                    >
                                        {isUpdating ? (
                                            <>
                                                <FaSpinner className="animate-spin w-5 h-5 mr-2" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <FaCheck className="w-5 h-5 mr-2" />
                                                Submit Update
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setUpdateModalOpen(false)}
                                        type="button"
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-100 transition duration-300 ease-in-out sm:mt-0 sm:w-auto sm:text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        <FaTimes className="w-5 h-5 mr-2" />
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </AuthGuard>
        </EmailGuard>
    );
};

export default PropertyWaterBillPage;